"use client";

import * as React from "react";
import { Minus, Plus, Tag, FolderOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();
  const [quantity, setQuantity] = React.useState(1);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-muted/30 animate-pulse rounded-lg" />
        <div className="space-y-6">
          <div className="h-12 bg-muted/30 animate-pulse rounded-md w-3/4" />
          <div className="h-8 bg-muted/30 animate-pulse rounded-md w-1/3" />
          <div className="h-32 bg-muted/30 animate-pulse rounded-md" />
          <div className="h-12 bg-muted/30 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Unable to load holiday details.</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">No holiday package available.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price < product.price;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
      {}
      <div className="relative">
        <div className="aspect-square overflow-hidden bg-card rounded-lg border border-border">
          {product.featured_image_url ? (
            <img
              src={product.featured_image_url}
              alt={product.title}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-muted-foreground">
              No image available
            </div>
          )}
        </div>
        {hasDiscount && (
          <Badge
            variant="default"
            className="absolute top-4 left-4 bg-accent-foreground text-background px-3 py-1 text-xs tracking-wider uppercase"
          >
            Special Offer
          </Badge>
        )}
      </div>

      {}
      <div className="flex flex-col">
        {}
        {product.categories && product.categories.length > 0 && (
          <div className="mb-4 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {product.categories[0].name}
          </div>
        )}

        {}
        <h1 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight mb-6">
          {product.title}
        </h1>

        {}
        <div className="flex items-baseline gap-4 mb-8 pb-8 border-b border-border">
          {hasDiscount ? (
            <>
              <span className="font-default text-3xl text-accent-foreground">
                ₹{product.discounted_price?.toLocaleString()}
              </span>
              <span className="text-xl text-muted-foreground line-through">
                ₹{product.price.toLocaleString()}
              </span>
            </>
          ) : (
            <span className="font-default text-3xl text-foreground">
              ₹{product.price.toLocaleString()}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-base text-muted-foreground leading-relaxed mb-8 [&_p]:mb-3 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        {}
        <div className="space-y-6 mb-10">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
              Travelers
            </label>
            <div className="flex items-center gap-0 border border-border w-fit rounded-lg">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="h-12 w-12 border-r border-border hover:bg-secondary"
                aria-label="Decrease travelers"
              >
                <Minus className="size-4" />
              </Button>
              <div className="h-12 w-16 flex items-center justify-center font-default text-lg">
                {quantity}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setQuantity(quantity + 1)}
                className="h-12 w-12 border-l border-border hover:bg-secondary"
                aria-label="Increase travelers"
              >
                <Plus className="size-4" />
              </Button>
            </div>
          </div>

          <AddToCartButton
            productId={product.id}
            variant="default"
            size="lg"
            className="w-full"
            buttonClassName="bg-primary text-primary-foreground w-full h-14 text-sm uppercase tracking-[0.2em] font-medium rounded-lg"
            text="Book Your Trip"
            showQuantity={false}
          />
        </div>

        {}
        <div className="space-y-4 pt-8 border-t border-border">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-3 text-sm">
              <FolderOpen className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground mr-3">
                  Destination
                </span>
                <span className="text-foreground">
                  {product.categories.map((cat, idx) => (
                    <React.Fragment key={cat.id}>
                      <Link
                        to={cat.link || "#"}
                        className="hover:text-accent-foreground transition-colors"
                      >
                        {cat.name}
                      </Link>
                      {idx < product.categories.length - 1 && (
                        <span className="text-muted-foreground">, </span>
                      )}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-3 text-sm">
              <Tag className="size-4 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground mr-3">
                  Trip Type
                </span>
                <span className="text-foreground">
                  {product.tags.map((tag, idx) => (
                    <React.Fragment key={tag.id}>
                      <Link
                        to={tag.link || "#"}
                        className="hover:text-accent-foreground transition-colors"
                      >
                        {tag.name}
                      </Link>
                      {idx < product.tags.length - 1 && (
                        <span className="text-muted-foreground">, </span>
                      )}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="lg:col-span-2 mt-12 pt-12 border-t border-border">
          <div className="max-w-4xl">
            <div className="mb-8">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
                Itinerary Details
              </div>
              <h2 className="font-default text-3xl md:text-4xl text-foreground">
                Experience Overview
              </h2>
            </div>
            <div
              className="text-base text-muted-foreground leading-relaxed [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:font-default [&_h2]:text-2xl [&_h2]:text-foreground [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:text-xl [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-2 [&_a]:text-accent-foreground [&_a]:underline hover:[&_a]:text-foreground"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function RelatedProductsConsumer() {
  const { products, loading } = useEcommerceProducts();
  const relatedProducts = products.slice(1, 5);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-80 bg-muted/30 animate-pulse rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (relatedProducts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
      {relatedProducts.map((product, i) => {
        const hasDiscount =
          product.discounted_price !== undefined &&
          product.discounted_price !== null &&
          product.discounted_price < product.price;

        return (
          <Card
            data-index={i}
            key={product.id}
            className="group bg-card border border-border overflow-hidden rounded-lg transition-all hover:shadow-lg"
          >
            <Link to={product.link || "#"} className="block">
              <div className="aspect-square overflow-hidden bg-secondary/30">
                {product.featured_image_url ? (
                  <img
                    src={product.featured_image_url}
                    alt={product.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-muted-foreground text-sm">
                    No image
                  </div>
                )}
              </div>
            </Link>
            <CardContent className="px-5 pb-5 space-y-3">
              {product.categories && product.categories.length > 0 && (
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {product.categories[0].name}
                </div>
              )}
              <Link to={product.link || "#"} className="block">
                <h3 className="font-default text-lg text-foreground leading-tight hover:text-accent-foreground transition-colors line-clamp-2">
                  {product.title}
                </h3>
              </Link>
              <div className="flex items-baseline gap-2 pt-1">
                {hasDiscount ? (
                  <>
                    <span className="font-default text-base text-accent-foreground">
                      ₹{product.discounted_price?.toLocaleString()}
                    </span>
                    <span className="text-sm text-muted-foreground line-through">
                      ₹{product.price.toLocaleString()}
                    </span>
                  </>
                ) : (
                  <span className="font-default text-base text-foreground">
                    ₹{product.price.toLocaleString()}
                  </span>
                )}
              </div>
              <AddToCartButton
                productId={product.id}
                variant="outline"
                size="sm"
                className="w-full"
                buttonClassName="bg-primary text-primary-foreground w-full text-xs uppercase tracking-[0.15em] rounded-lg"
                text="View Details"
                showQuantity={false}
              />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  const productQuery = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="17879"
      id="single-product-template-section"
      className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 lg:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <EcommerceProductsProvider wp_query={productQuery}>
          {}
          <SingleProductConsumer />

          {}
          <div className="mt-24 md:mt-32 pt-16 border-t border-border">
            <div className="mb-12 text-center">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">
                Explore More
              </div>
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl text-foreground">
                Recommended Destinations
              </h2>
              <div className="mt-6 mx-auto w-16 h-px bg-accent-foreground/40" />
            </div>

            <RelatedProductsConsumer />
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}